import type { MailSyncStatus } from "@/lib/types";

/** "3 Nachrichten · mit Sabrina Schneider" */
export function mailMeta(item: { entryCount: number; with: string[] }): string {
  const count = `${item.entryCount} ${item.entryCount === 1 ? "Nachricht" : "Nachrichten"}`;
  return item.with.length ? `${count} · mit ${item.with.join(", ")}` : count;
}

/** The thread's link into the HEY web app, or null (rendered without a link) for anything that isn't https://app.hey.com/…. */
export function heyLink(url: string): string | null {
  try {
    return new URL(url).origin === "https://app.hey.com" ? url : null;
  } catch {
    return null;
  }
}

/** Short relative time for the sync line. */
export function ago(iso: string, now: Date): string {
  const min = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60_000));
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return d === 1 ? "vor 1 Tag" : `vor ${d} Tagen`;
}

export type SyncTone = "muted" | "ok" | "error";

/** The Firmen header's sync line. A warning after a successful run goes into `detail` (tooltip), not the line. */
export function syncLabel(s: MailSyncStatus, now: Date): { text: string; tone: SyncTone; detail: string | null } {
  if (!s.configured) return { text: "HEY nicht verbunden", tone: "muted", detail: null };
  if (s.running) return { text: "Mails werden abgeglichen…", tone: "muted", detail: null };
  if (s.lastRunAt && s.lastError && s.lastRunAt !== s.lastOkAt) return { text: `Abgleich fehlgeschlagen: ${s.lastError}`, tone: "error", detail: null };
  if (s.lastOkAt) return { text: `Mails abgeglichen ${ago(s.lastOkAt, now)}`, tone: "ok", detail: s.lastError };
  return { text: "Noch nicht abgeglichen", tone: "muted", detail: null };
}

/** "Import unvollständig: …" while the sticky truncation warning is set, else null. */
export function importWarning(s: MailSyncStatus): string | null {
  return s.lastWarning ? `Import unvollständig: ${s.lastWarning}` : null;
}

/** HEY is working on something the detail page waits for: a run, or addresses queued for their 12-month backfill. */
export function syncBusy(s: MailSyncStatus | undefined): boolean {
  return !!s && s.configured && (s.running || s.pending.length > 0);
}

/**
 * One of this company's addresses still waits for (or is in) its 12-month backfill. Queued addresses leave
 * `pending` only when their import succeeded, so this stays true while HEY is being read.
 */
export function fetchingMail(s: MailSyncStatus | undefined, addresses: string[]): boolean {
  if (!s?.configured || s.pending.length === 0) return false;
  const own = new Set(addresses.map((a) => a.toLowerCase()));
  return s.pending.some((p) => own.has(p.toLowerCase()));
}

export interface AddressChip { email: string; removable: boolean; via: string | null }

/** The company's own addresses (removable) first, then contact e-mails not already listed (muted, "über Kontakt"). */
export function addressChips(emails: string[], contacts: { name: string; email: string | null }[]): AddressChip[] {
  const out: AddressChip[] = emails.map((email) => ({ email, removable: true, via: null }));
  for (const c of contacts) {
    const email = c.email?.trim().toLowerCase();
    if (email && !out.some((x) => x.email === email)) out.push({ email, removable: false, via: c.name });
  }
  return out;
}
